import type { ApiErrorBody, AppErrorCode } from '@cfcg/shared';

/**
 * Błąd z API niosący stabilny `code`. Front dobiera reakcję po kodzie,
 * a użytkownikowi pokazuje gotowy `messagePl` — nigdy nie skleja komunikatu
 * z treści wyjątku.
 */
export class ApiError extends Error {
  readonly code: AppErrorCode | 'NETWORK';
  readonly status: number;
  readonly details: unknown;

  constructor(body: ApiErrorBody | { code: 'NETWORK'; messagePl: string }, status: number) {
    super(body.messagePl);
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.details = 'details' in body ? body.details : undefined;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Cookie sesji jest HttpOnly i same-origin — musi jechać z każdym żądaniem.
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(
      { code: 'NETWORK', messagePl: 'Brak połączenia z serwerem. Sprawdź sieć.' },
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => null)) as unknown;

  if (!response.ok) {
    const parsed =
      body !== null && typeof body === 'object' && 'code' in body
        ? (body as ApiErrorBody)
        : { code: 'INTERNAL' as const, messagePl: 'Wystąpił nieoczekiwany błąd.' };
    throw new ApiError(parsed, response.status);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
